import React, { useState } from "react";
import ImageUploadField from "@/components/ImageUploadField";
import { normalizeImageUrl } from "@/lib/imageUrl";
import { swatchColor } from "@/lib/colorSwatch";
import { toast } from "sonner";
import { Star, Trash2, ArrowUp, ArrowDown, GripVertical, Copy, Plus, ChevronDown, ChevronRight } from "lucide-react";

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

// Reusable image grid: drag-drop + up/down reorder, set primary, delete, add.
function ImageGrid({ images, onChange, productName, testidPrefix, usageCount }) {
  const [drag, setDrag] = useState(null);
  const list = images || [];

  const commit = (next) => {
    // keep primary flag single + reindex order
    const hasPrimary = next.some((i) => i.is_primary);
    onChange(next.map((i, idx) => ({ ...i, order: idx, is_primary: hasPrimary ? i.is_primary : idx === 0 })));
  };
  const move = (from, to) => {
    if (to < 0 || to >= list.length) return;
    const next = list.slice();
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    commit(next);
  };
  const setPrimary = (idx) => commit(list.map((i, x) => ({ ...i, is_primary: x === idx })));
  const remove = (idx) => {
    const img = list[idx];
    if (usageCount && usageCount(img.url) > 1 && !window.confirm("This image is used in more than one place. Remove it here anyway?")) return;
    commit(list.filter((_, x) => x !== idx));
  };
  const add = (url) => { if (url) commit([...list, { id: uid(), url, alt: productName, is_primary: list.length === 0, order: list.length }]); };

  return (
    <div>
      {list.length > 0 && (
        <div className="grid grid-cols-4 gap-2" data-testid={`${testidPrefix}-grid`}>
          {list.map((img, i) => (
            <div key={img.id || i} draggable onDragStart={() => setDrag(i)}
              onDragOver={(e) => e.preventDefault()} onDrop={() => { if (drag != null) move(drag, i); setDrag(null); }}
              data-testid={`${testidPrefix}-item-${i}`}
              className={`relative border rounded-sm overflow-hidden group ${img.is_primary ? "border-[#E63946]" : "border-slate-200"}`}>
              <img src={normalizeImageUrl(img.url)} alt={img.alt || ""} className="w-full aspect-square object-cover" />
              <span className="absolute top-1 left-1 text-white/80 cursor-grab"><GripVertical className="w-3.5 h-3.5 drop-shadow" /></span>
              {img.is_primary && <span className="absolute bottom-1 left-1 bg-[#E63946] text-white text-[8px] px-1 rounded-sm">PRIMARY</span>}
              <div className="absolute inset-x-0 top-1 right-1 flex justify-end gap-1">
                <button type="button" title="Set primary" onClick={() => setPrimary(i)} data-testid={`${testidPrefix}-primary-${i}`}
                  className="bg-white/90 rounded-full p-1"><Star className={`w-3 h-3 ${img.is_primary ? "text-[#E63946] fill-[#E63946]" : "text-slate-500"}`} /></button>
                <button type="button" title="Delete" onClick={() => remove(i)} data-testid={`${testidPrefix}-del-${i}`}
                  className="bg-white/90 rounded-full p-1"><Trash2 className="w-3 h-3 text-[#E63946]" /></button>
              </div>
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-white/70 opacity-0 group-hover:opacity-100 transition-opacity py-0.5">
                <button type="button" title="Move left" onClick={() => move(i, i - 1)} className="p-0.5"><ArrowUp className="w-3 h-3 -rotate-90 text-slate-600" /></button>
                <button type="button" title="Move right" onClick={() => move(i, i + 1)} className="p-0.5"><ArrowDown className="w-3 h-3 -rotate-90 text-slate-600" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2">
        <ImageUploadField label="Add image" value="" onChange={add} testid={`${testidPrefix}-upload`} aspect="aspect-square" />
      </div>
    </div>
  );
}

export default function MarketplaceImageManager({ productName, images, setImages, imageGroup, setImageGroup, galleries, setGalleries, optionNames, optionValues }) {
  const [open, setOpen] = useState({});

  // how many times a url appears across product images + all galleries
  const usageCount = (url) => {
    let n = (images || []).filter((i) => i.url === url).length;
    (galleries || []).forEach((g) => { n += (g.images || []).filter((i) => i.url === url).length; });
    return n;
  };

  const galleryFor = (val) => (galleries || []).find((g) => g.option_name === imageGroup && g.option_value === val);
  const setGalleryImages = (val, imgs) => {
    const rest = (galleries || []).filter((g) => !(g.option_name === imageGroup && g.option_value === val));
    const existing = galleryFor(val) || { option_name: imageGroup, option_value: val };
    setGalleries([...rest, { ...existing, images: imgs }]);
  };
  const setSwatch = (val, hex) => {
    const rest = (galleries || []).filter((g) => !(g.option_name === imageGroup && g.option_value === val));
    const existing = galleryFor(val) || { option_name: imageGroup, option_value: val, images: [] };
    setGalleries([...rest, { ...existing, swatch_hex: hex }]);
  };
  const assignExisting = (val) => {
    const g = galleryFor(val);
    const current = g?.images || [];
    const toAdd = (images || []).filter((pi) => !current.some((ci) => ci.url === pi.url))
      .map((pi, idx) => ({ id: uid(), url: pi.url, alt: pi.alt, is_primary: current.length === 0 && idx === 0, order: current.length + idx }));
    if (!toAdd.length) { toast.info("All product images already assigned"); return; }
    setGalleryImages(val, [...current, ...toAdd]);
    toast.success(`Assigned ${toAdd.length} image(s)`);
  };
  const copyFrom = (val, srcVal) => {
    const src = galleryFor(srcVal);
    if (!src?.images?.length) { toast.error("Source colour has no images"); return; }
    setGalleryImages(val, src.images.map((i, idx) => ({ ...i, id: uid(), order: idx })));
    toast.success(`Copied gallery from ${srcVal}`);
  };

  return (
    <div className="space-y-5" data-testid="mp-image-manager">
      {/* Default product gallery */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="label-overline text-[10px] mb-1">Product Images (shared / default)</div>
        <p className="text-[11px] text-slate-400 mb-3">Shown for any colour without its own gallery. Set a primary, drag to reorder.</p>
        <ImageGrid images={images} onChange={setImages} productName={productName} testidPrefix="mp-pimg" usageCount={usageCount} />
      </div>

      {/* Image-driving option + per-value galleries */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="label-overline text-[10px]">Colour / Variant Galleries</div>
          <label className="text-xs text-slate-500 flex items-center gap-2">
            Image-driving option
            <select data-testid="mp-image-group" value={imageGroup || ""} onChange={(e) => setImageGroup(e.target.value || null)}
              className="border border-slate-300 rounded-sm px-2 py-1 text-sm bg-white">
              <option value="">None</option>
              {optionNames.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
        {!imageGroup ? <p className="text-[11px] text-slate-400 mt-2">Pick an option (e.g. Colour) to give each value its own image gallery.</p>
          : optionValues.length === 0 ? <p className="text-[11px] text-slate-400 mt-2">Add variants with a “{imageGroup}” value to manage per-{imageGroup} galleries.</p>
          : (
            <div className="mt-3 space-y-2">
              {optionValues.map((val) => {
                const g = galleryFor(val);
                const hex = swatchColor(val, g?.swatch_hex);
                const isOpen = open[val];
                return (
                  <div key={val} className="border border-slate-200 rounded-sm" data-testid={`mp-gallery-${val}`}>
                    <button type="button" onClick={() => setOpen((o) => ({ ...o, [val]: !o[val] }))}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left">
                      {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      <span className="w-5 h-5 rounded-full border border-slate-300" style={{ backgroundColor: hex }} />
                      <span className="text-sm font-semibold text-[#0A192F]">{val}</span>
                      <span className="text-[11px] text-slate-400">{g?.images?.length || 0} image(s){g?.images?.length ? "" : " — uses default"}</span>
                    </button>
                    {isOpen && (
                      <div className="px-3 pb-3 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="text-xs text-slate-500 flex items-center gap-1">Swatch
                            <input type="color" value={/^#/.test(hex) ? hex : "#cccccc"} onChange={(e) => setSwatch(val, e.target.value)}
                              data-testid={`mp-swatch-${val}`} className="w-8 h-7 border border-slate-300 rounded-sm" /></label>
                          <button type="button" onClick={() => assignExisting(val)} data-testid={`mp-assign-${val}`}
                            className="inline-flex items-center gap-1 border border-slate-300 text-slate-600 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider rounded-sm"><Plus className="w-3 h-3" /> Assign product images</button>
                          <div className="inline-flex items-center gap-1">
                            <Copy className="w-3 h-3 text-slate-400" />
                            <select data-testid={`mp-copy-${val}`} value="" onChange={(e) => { if (e.target.value) copyFrom(val, e.target.value); e.target.value = ""; }}
                              className="border border-slate-300 rounded-sm px-1.5 py-1 text-[11px] bg-white">
                              <option value="">Copy from…</option>
                              {optionValues.filter((o) => o !== val).map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                        </div>
                        <ImageGrid images={g?.images || []} onChange={(imgs) => setGalleryImages(val, imgs)} productName={`${productName} — ${val}`} testidPrefix={`mp-gimg-${val}`} usageCount={usageCount} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}
